import React from 'react';
import { HiExclamationTriangle, HiCheckCircle } from 'react-icons/hi2';
import { Textarea } from '@/components';

interface AutorizacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  action: 'aprovar' | 'reprovar' | null;
  isProcessing: boolean;
  motivo?: string;
  onMotivoChange?: (value: string) => void;
}

export const AutorizacaoModal: React.FC<AutorizacaoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  isProcessing,
  motivo = '',
  onMotivoChange,
}) => {
  if (!action || !isOpen) return null;

  const isAprovar = action === 'aprovar';

  // Configurações específicas para cada tipo de ação
  const config = isAprovar ? {
    title: 'Aprovar Autorização',
    subtitle: 'Descreva o motivo da aprovação (opcional)',
    icon: HiCheckCircle,
    iconBgColor: 'bg-green-100 dark:bg-green-900/20',
    iconColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-200 dark:border-green-800',
    alertBgColor: 'bg-green-50 dark:bg-green-900/20',
    alertBorderColor: 'border-green-200 dark:border-green-800',
    alertTextColor: 'text-green-800 dark:text-green-200',
    alertMessage: 'Após a aprovação, a demanda seguirá para o próximo autorizador (se houver) ou será liberada para atribuição.',
    buttonColor: 'bg-green-600 hover:bg-green-700',
    buttonText: 'Aprovar Autorização',
    processingText: 'Aprovando...',
    placeholderText: 'Explique por que esta demanda está sendo aprovada...\n\nExemplo:\n- Requisitos atendem às políticas da empresa\n- Documentação completa e adequada\n- Orçamento aprovado',
    helperText: ''
  } : {
    title: 'Reprovar Autorização',
    subtitle: 'Descreva o motivo da reprovação (opcional)',
    icon: HiExclamationTriangle,
    iconBgColor: 'bg-red-100 dark:bg-red-900/20',
    iconColor: 'text-red-600 dark:text-red-400',
    borderColor: 'border-red-200 dark:border-red-800',
    alertBgColor: 'bg-red-50 dark:bg-red-900/20',
    alertBorderColor: 'border-red-200 dark:border-red-800',
    alertTextColor: 'text-red-800 dark:text-red-200',
    alertMessage: 'Ao reprovar, a demanda será bloqueada permanentemente e não poderá ser executada sem uma nova solicitação.',
    buttonColor: 'bg-red-600 hover:bg-red-700',
    buttonText: 'Reprovar Autorização',
    processingText: 'Reprovando...',
    placeholderText: 'Explique por que esta demanda não pode ser aprovada...\n\nExemplo:\n- Requisitos não atendem às políticas da empresa\n- Documentação incompleta\n- Orçamento insuficiente',
    helperText: ''
  };

  const IconComponent = config.icon;

  return (
    <>
      {/* Modal customizado com textarea para Aprovação e Reprovação */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className={`bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-lg w-full p-6 border-2 ${config.borderColor}`}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 ${config.iconBgColor} rounded-lg`}>
                <IconComponent className={`w-6 h-6 ${config.iconColor}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {config.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {config.subtitle}
                </p>
              </div>
            </div>

            {/* Alerta de Aviso */}
            <div className={`${config.alertBgColor} border ${config.alertBorderColor} rounded-lg p-4 mb-4`}>
              <p className={`text-sm ${config.alertTextColor}`}>
                <strong>⚠️ Atenção:</strong> {config.alertMessage}
              </p>
            </div>

            <div className="space-y-4">
              {/* Campo de Motivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isAprovar ? 'Justificativa da Aprovação' : 'Motivo da Reprovação'}
                </label>
                <Textarea
                  variant="simple"
                  value={motivo}
                  onChange={(value) => onMotivoChange?.(value)}
                  placeholder={config.placeholderText}
                  rows={6}
                  className="w-full"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {config.helperText}
                  </p>
                  {motivo.trim().length > 0 && (
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {motivo.trim().length} caracteres
                    </p>
                  )}
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onClose}
                  disabled={isProcessing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isProcessing}
                  className={`px-4 py-2 text-sm font-medium text-white ${config.buttonColor} disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2`}
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {config.processingText}
                    </>
                  ) : (
                    config.buttonText
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
